import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCalendarCheck,
  faUserDoctor,
  faSpinner,
  faExclamationTriangle,
  faCircleCheck,
  faTimesCircle,
  faEye,
  faPhone,
  faEnvelope,
  faMapMarkerAlt,
  faClock,
  faCalendarDay,
  faFilter,
  faSearch,
  faDownload,
  faChartLine,
  faUsers,
  faCheckCircle,
  faBan,
  faHourglassHalf,
  faStethoscope,
  faStar,
  faInfoCircle,
  faUserPlus,
  faCalendarPlus
} from "@fortawesome/free-solid-svg-icons";
import { bookingService } from "../../lib/supabase";

const BookingsManager = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [updating, setUpdating] = useState(false);

  // Load bookings and stats
  useEffect(() => {
    loadBookings();
    loadStats();
  }, [filterStatus]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadBookings = async () => {
    try {
      setLoading(true);
      const data = await bookingService.getAllBookings(
        filterStatus === "all" ? null : filterStatus
      );
      setBookings(data);
    } catch (err) {
      setError("Eroare la încărcarea programărilor");
      console.error("Error loading bookings:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const statsData = await bookingService.getBookingStats();
      setStats(statsData);
    } catch (err) {
      console.error("Error loading stats:", err);
    }
  };

  const handleStatusUpdate = async (bookingId, newStatus, adminNotes = null) => {
    try {
      setUpdating(true);
      await bookingService.updateBookingStatus(bookingId, newStatus, adminNotes);
      await loadBookings();
      await loadStats();
      setSelectedBooking(null);
    } catch (err) {
      alert("Eroare la actualizarea statusului");
      console.error("Error updating status:", err);
    } finally {
      setUpdating(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString("ro-RO", {
      weekday: "short",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const formatTime = (timeStr) => {
    return timeStr ? timeStr.slice(0, 5) : "N/A";
  };



  const getStatusIcon = (status) => {
    switch (status) {
      case "pending":
        return faHourglassHalf;
      case "confirmed":
        return faCircleCheck;
      case "completed":
        return faCheckCircle;
      case "cancelled":
        return faBan;
      default:
        return faClock;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "pending":
        return "În așteptare";
      case "confirmed":
        return "Confirmată";
      case "completed":
        return "Finalizată";
      case "cancelled":
        return "Anulată";
      default:
        return status;
    }
  };

  const filteredBookings = bookings.filter((booking) => {
    const searchMatch =
      booking.first_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.last_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      booking.phone?.includes(searchTerm) ||
      booking.doctor_name?.toLowerCase().includes(searchTerm.toLowerCase());

    return searchMatch;
  });

  if (loading) {
    return (
      <div className="admin-content">
        <div className="booking-loading-container">
          <div className="booking-loading-content">
            <div className="loading-spinner">
              <FontAwesomeIcon icon={faSpinner} spin size="3x" />
            </div>
            <h3>Se încarcă programările...</h3>
            <p>Vă rugăm să așteptați</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-content">
        <div className="booking-error-container">
          <div className="booking-error-content">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              size="3x"
              className="error-icon"
            />
            <h3>Oops! Ceva nu a mers bine</h3>
            <p className="error-message">{error}</p>
            <button className="btn-retry" onClick={loadBookings}>
              <FontAwesomeIcon icon={faSpinner} className="me-2" />
              Încearcă din nou
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="admin-content booking-manager-wrapper">
      {/* Enhanced Header */}
      <div className="booking-header-section">
        <div className="header-background-pattern"></div>
        <div className="booking-header-content">
          <div className="header-icon-wrapper">
            <FontAwesomeIcon icon={faCalendarCheck} className="header-main-icon" />
          </div>
          <div className="header-text">
            <h1 className="page-title">
              Managementul Programărilor
              <span className="title-accent"></span>
            </h1>
            <p className="page-subtitle">
              Gestionează eficient programările pacienților și monitorizează statusurile acestora
            </p>
          </div>
          <button className="btn-refresh" onClick={loadBookings}>
            <FontAwesomeIcon icon={faSpinner} className="refresh-icon" />
            <span>Actualizează</span>
          </button>
        </div>
      </div>

      {/* Enhanced Statistics Cards */}
      {stats && (
        <div className="stats-section">
          <div className="stats-grid">
            <div className="stat-card total-card">
              <div className="stat-icon-wrapper">
                <FontAwesomeIcon icon={faUsers} className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.total}</div>
                <div className="stat-label">Total Programări</div>
              </div>
              <div className="stat-trend">
                <FontAwesomeIcon icon={faChartLine} />
              </div>
            </div>

            <div className="stat-card pending-card">
              <div className="stat-icon-wrapper">
                <FontAwesomeIcon icon={faHourglassHalf} className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.pending}</div>
                <div className="stat-label">În așteptare</div>
              </div>
              <div className="stat-trend">
                <FontAwesomeIcon icon={faExclamationTriangle} />
              </div>
            </div>

            <div className="stat-card confirmed-card">
              <div className="stat-icon-wrapper">
                <FontAwesomeIcon icon={faCircleCheck} className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.confirmed}</div>
                <div className="stat-label">Confirmate</div>
              </div>
              <div className="stat-trend">
                <FontAwesomeIcon icon={faCalendarCheck} />
              </div>
            </div>

            <div className="stat-card completed-card">
              <div className="stat-icon-wrapper">
                <FontAwesomeIcon icon={faCheckCircle} className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.completed}</div>
                <div className="stat-label">Finalizate</div>
              </div>
              <div className="stat-trend">
                <FontAwesomeIcon icon={faStar} />
              </div>
            </div>

            <div className="stat-card cancelled-card">
              <div className="stat-icon-wrapper">
                <FontAwesomeIcon icon={faBan} className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.cancelled}</div>
                <div className="stat-label">Anulate</div>
              </div>
              <div className="stat-trend">
                <FontAwesomeIcon icon={faTimesCircle} />
              </div>
            </div>

            <div className="stat-card weekly-card">
              <div className="stat-icon-wrapper">
                <FontAwesomeIcon icon={faCalendarPlus} className="stat-icon" />
              </div>
              <div className="stat-content">
                <div className="stat-number">{stats.thisWeek}</div>
                <div className="stat-label">Săptămâna aceasta</div>
              </div>
              <div className="stat-trend">
                <FontAwesomeIcon icon={faUserPlus} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Enhanced Filters and Search */}
      <div className="filters-section">
        <div className="filters-card">
          <div className="search-wrapper">
            <div className="search-input-container">
              <FontAwesomeIcon icon={faSearch} className="search-icon" />
              <input
                type="text"
                className="search-input"
                placeholder="Caută după nume, email, telefon sau doctor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button 
                  className="clear-search"
                  onClick={() => setSearchTerm('')}
                >
                  <FontAwesomeIcon icon={faTimesCircle} />
                </button>
              )}
            </div>
          </div>

          <div className="filter-wrapper">
            <div className="filter-dropdown-container">
              <FontAwesomeIcon icon={faFilter} className="filter-icon" />
              <select
                className="filter-select"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="all">Toate statusurile</option>
                <option value="pending">În așteptare</option>
                <option value="confirmed">Confirmate</option>
                <option value="completed">Finalizate</option>
                <option value="cancelled">Anulate</option>
              </select>
            </div>
          </div>

          <div className="export-wrapper">
            <button className="btn-export">
              <FontAwesomeIcon icon={faDownload} className="export-icon" />
              <span>Exportă CSV</span>
            </button>
          </div>
        </div>

        {/* Results Summary */}
        <div className="results-summary">
          <div className="results-info">
            <FontAwesomeIcon icon={faInfoCircle} className="info-icon" />
            <span>
              Se afișează <strong>{filteredBookings.length}</strong> din <strong>{bookings.length}</strong> programări
              {searchTerm && ` pentru "${searchTerm}"`}
            </span>
          </div>
        </div>
      </div>

      {/* Enhanced Bookings Table */}
      <div className="bookings-table-section">
        <div className="table-card">
          <div className="table-header">
            <h3>
              <FontAwesomeIcon icon={faStethoscope} className="table-icon" />
              Lista Programărilor
            </h3>
          </div>
          
          <div className="table-container">
            <div className="table-responsive">
              <table className="bookings-table">
                <thead>
                  <tr>
                    <th className="col-patient">
                      <FontAwesomeIcon icon={faUserDoctor} className="th-icon" />
                      Pacient
                    </th>
                    <th className="col-contact">
                      <FontAwesomeIcon icon={faEnvelope} className="th-icon" />
                      Contact
                    </th>
                    <th className="col-doctor">
                      <FontAwesomeIcon icon={faStethoscope} className="th-icon" />
                      Doctor
                    </th>
                    <th className="col-datetime">
                      <FontAwesomeIcon icon={faCalendarDay} className="th-icon" />
                      Data/Ora
                    </th>
                    <th className="col-status">
                      <FontAwesomeIcon icon={faCircleCheck} className="th-icon" />
                      Status
                    </th>
                    <th className="col-created">
                      <FontAwesomeIcon icon={faClock} className="th-icon" />
                      Creat
                    </th>
                    <th className="col-actions">Acțiuni</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredBookings.length === 0 ? (
                    <tr>
                      <td colSpan="7" className="empty-state">
                        <div className="empty-content">
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            className="empty-icon"
                          />
                          <h4>Nu există programări</h4>
                          <p>Nu sunt programări care să corespundă criteriilor selectate</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredBookings.map((booking) => (
                      <tr key={booking.id} className="booking-row">
                        <td className="patient-cell">
                          <div className="patient-info">
                            <div className="patient-avatar">
                              <FontAwesomeIcon icon={faUserDoctor} />
                            </div>
                            <div className="patient-details">
                              <strong className="patient-name">
                                {booking.first_name} {booking.last_name}
                              </strong>
                              <small className="booking-id">
                                ID: {booking.id.slice(0, 8)}...
                              </small>
                            </div>
                          </div>
                        </td>
                        
                        <td className="contact-cell">
                          <div className="contact-info">
                            <div className="contact-item">
                              <FontAwesomeIcon icon={faEnvelope} className="contact-icon" />
                              <span className="contact-text">{booking.email}</span>
                            </div>
                            <div className="contact-item">
                              <FontAwesomeIcon icon={faPhone} className="contact-icon" />
                              <span className="contact-text">{booking.phone}</span>
                            </div>
                          </div>
                        </td>
                        
                        <td className="doctor-cell">
                          <div className="doctor-info">
                            <div className="doctor-name">{booking.doctor_name}</div>
                            <div className="location-info">
                              <FontAwesomeIcon
                                icon={faMapMarkerAlt}
                                className="location-icon"
                              />
                              <span className="location-text">{booking.location_name}</span>
                            </div>
                          </div>
                        </td>
                        
                        <td className="datetime-cell">
                          <div className="datetime-info">
                            <div className="date-info">
                              <FontAwesomeIcon icon={faCalendarDay} className="datetime-icon" />
                              <span>{formatDate(booking.preferred_date)}</span>
                            </div>
                            <div className="time-info">
                              <FontAwesomeIcon icon={faClock} className="datetime-icon" />
                              <span>{formatTime(booking.preferred_time)}</span>
                            </div>
                          </div>
                        </td>
                        
                        <td className="status-cell">
                          <span
                            className={`status-badge status-${booking.status}`}
                          >
                            <FontAwesomeIcon
                              icon={getStatusIcon(booking.status)}
                              className="status-icon"
                            />
                            {getStatusLabel(booking.status)}
                          </span>
                        </td>
                        
                        <td className="created-cell">
                          <div className="created-date">
                            {formatDate(booking.created_at)}
                          </div>
                        </td>
                        
                        <td className="actions-cell">
                          <div className="action-buttons">
                            <button
                              className="action-btn view-btn"
                              onClick={() => setSelectedBooking(booking)}
                              title="Vezi detalii"
                            >
                              <FontAwesomeIcon icon={faEye} />
                            </button>
                            
                            {booking.status === "pending" && (
                              <>
                                <button
                                  className="action-btn confirm-btn"
                                  onClick={() =>
                                    handleStatusUpdate(booking.id, "confirmed")
                                  }
                                  disabled={updating}
                                  title="Confirmă"
                                >
                                  <FontAwesomeIcon icon={faCircleCheck} />
                                </button>
                                <button
                                  className="action-btn cancel-btn"
                                  onClick={() =>
                                    handleStatusUpdate(booking.id, "cancelled")
                                  }
                                  disabled={updating}
                                  title="Anulează"
                                >
                                  <FontAwesomeIcon icon={faBan} />
                                </button>
                              </>
                            )}
                            
                            {booking.status === "confirmed" && (
                              <button
                                className="action-btn complete-btn"
                                onClick={() =>
                                  handleStatusUpdate(booking.id, "completed")
                                }
                                disabled={updating}
                                title="Marchează ca finalizată"
                              >
                                <FontAwesomeIcon icon={faCheckCircle} />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* Enhanced Booking Details Modal */}
      {selectedBooking && (
        <div className="booking-modal-overlay">
          <div className="booking-modal">
            <div className="modal-header">
              <div className="modal-header-content">
                <div className="modal-icon">
                  <FontAwesomeIcon icon={faUserDoctor} />
                </div>
                <div className="modal-title-section">
                  <h2 className="modal-title">Detalii Programare</h2>
                  <p className="modal-subtitle">
                    {selectedBooking.first_name} {selectedBooking.last_name}
                  </p>
                </div>
              </div>
              <button
                className="modal-close-btn"
                onClick={() => setSelectedBooking(null)}
              >
                <FontAwesomeIcon icon={faTimesCircle} />
              </button>
            </div>
            
            <div className="modal-body">
              <div className="modal-content-grid">
                <div className="info-section patient-section">
                  <h3 className="section-title">
                    <FontAwesomeIcon icon={faUserDoctor} className="section-icon" />
                    Informații Pacient
                  </h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Nume complet:</span>
                      <span className="info-value">
                        {selectedBooking.first_name} {selectedBooking.last_name}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Email:</span>
                      <span className="info-value">{selectedBooking.email}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Telefon:</span>
                      <span className="info-value">{selectedBooking.phone}</span>
                    </div>
                  </div>
                </div>

                <div className="info-section appointment-section">
                  <h3 className="section-title">
                    <FontAwesomeIcon icon={faCalendarCheck} className="section-icon" />
                    Detalii Programare
                  </h3>
                  <div className="info-grid">
                    <div className="info-item">
                      <span className="info-label">Doctor:</span>
                      <span className="info-value">{selectedBooking.doctor_name}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Clinica:</span>
                      <span className="info-value">{selectedBooking.location_name}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Data:</span>
                      <span className="info-value">
                        {formatDate(selectedBooking.preferred_date)}
                      </span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Ora:</span>
                      <span className="info-value">
                        {formatTime(selectedBooking.preferred_time)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="symptoms-section">
                <h3 className="section-title">
                  <FontAwesomeIcon icon={faStethoscope} className="section-icon" />
                  Simptome/Motivul Consultației
                </h3>
                <div className="symptoms-content">
                  {selectedBooking.symptoms}
                </div>
              </div>

              {selectedBooking.admin_notes && (
                <div className="notes-section">
                  <h3 className="section-title">
                    <FontAwesomeIcon icon={faInfoCircle} className="section-icon" />
                    Note Administrative
                  </h3>
                  <div className="notes-content">
                    {selectedBooking.admin_notes}
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-footer">
              <button
                className="btn-modal-secondary"
                onClick={() => setSelectedBooking(null)}
              >
                Închide
              </button>
              <a
                href={`tel:${selectedBooking.phone}`}
                className="btn-modal-primary"
              >
                <FontAwesomeIcon icon={faPhone} className="btn-icon" />
                Sună Pacientul
              </a>
            </div>
          </div>
        </div>
      )}

      {/* Loading Overlay for Updates */}
      {updating && (
        <div className="update-loading-overlay">
          <div className="update-loading-content">
            <FontAwesomeIcon icon={faSpinner} spin size="2x" />
            <p>Se actualizează...</p>
          </div>
        </div>
      )}
    </div>
  );
};

export default BookingsManager; 